import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../api/axiosConfig";
import { toast } from "react-toastify";

const TimesheetContext = createContext();

export const useTimesheets = () => useContext(TimesheetContext);

export const TimesheetProvider = ({ children }) => {
  const [timesheets, setTimesheets] = useState([]);
  const [loading, setLoading] = useState(false);

  // 1. Fetch all timesheets from backend database
  const fetchTimesheets = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/timesheets");
      const list = res.data?.data || res.data?.logs || (Array.isArray(res.data) ? res.data : []);
      setTimesheets(list);
    } catch (err) {
      console.error("Error fetching timesheets from database:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // 2. Fetch logs for a specific task
  const fetchTaskLogs = useCallback(async (taskId) => {
    if (!taskId) return [];
    try {
      const res = await api.get(`/tasks/${taskId}/logs`);
      const list = res.data?.data || res.data?.logs || (Array.isArray(res.data) ? res.data : []);
      setTimesheets(prev => {
        const others = prev.filter(t => String(t.taskId || t.task_id) !== String(taskId));
        return [...list, ...others];
      });
      return list;
    } catch (err) {
      console.error(`Error fetching logs for task ${taskId}:`, err);
      return [];
    }
  }, []);

  useEffect(() => {
    fetchTimesheets();
  }, [fetchTimesheets]);

  // 3. Add Log to Database
  const addLog = async (logData) => {
    try {
      const taskId = logData.taskId;
      const url = taskId ? `/tasks/${taskId}/logs` : `/timesheets`;
      const res = await api.post(url, logData);
      
      const created = res.data?.log || res.data?.data || {
        ...logData,
        id: res.data?.id || Date.now(),
        createdAt: new Date().toISOString()
      };

      setTimesheets(prev => [created, ...prev]);
      return created;
    } catch (err) {
      console.error("Error saving log to database:", err);
      toast.error(err.response?.data?.error || "Failed to save log to database");
      throw err;
    }
  };

  // 4. Delete Log from Database
  const deleteLog = async (logId, taskId) => {
    try {
      const url = taskId ? `/tasks/${taskId}/logs/${logId}` : `/timesheets/${logId}`;
      await api.delete(url);
      setTimesheets(prev => prev.filter(t => String(t.id) !== String(logId)));
      toast.success("Time log deleted");
      return true;
    } catch (err) {
      console.error("Error deleting log:", err);
      toast.error(err.response?.data?.error || "Failed to delete log");
      throw err;
    }
  };

  const getTaskLogs = (taskId) => {
    return timesheets
      .filter(log => String(log.taskId || log.task_id) === String(taskId))
      .sort((a, b) => new Date(b.createdAt || b.created_at || b.date) - new Date(a.createdAt || a.created_at || a.date));
  };

  // Get labor for a specific Cost Sheet
  const getCostSheetLogs = (costSheetId) => {
    return timesheets.filter(log => String(log.costSheetId || log.cost_sheet_id) === String(costSheetId));
  };

  const getTaskTotalHours = (taskId) => {
    const logs = getTaskLogs(taskId);
    return logs.reduce((sum, log) => sum + (Number(log.hours) || 0), 0);
  };

  return (
    <TimesheetContext.Provider value={{ 
      timesheets, 
      loading,
      fetchTimesheets,
      fetchTaskLogs,
      addLog, 
      deleteLog,
      getTaskLogs, 
      getTaskTotalHours, 
      getCostSheetLogs 
    }}>
      {children}
    </TimesheetContext.Provider>
  );
};